<template>
	<div class="input-state">
		<header class="page-header">
			<h1>{{ t("contactResearchPage.title") }}</h1>
			<p>{{ t("contactResearchPage.description") }}</p>
		</header>

		<div v-if="providerLoadError" class="alert error" role="alert">{{ providerLoadError }}</div>
		<div v-if="error" class="alert error" role="alert">{{ error }}</div>

		<form @submit.prevent="runSearch">
			<section class="panel">
				<div class="fields">
					<label>
						<span>{{ t("contactResearchPage.form.firstName") }}{{ contactNameRequired ? " *" : "" }}</span>
						<input v-model="draft.firstName" :required="contactNameRequired" autocomplete="given-name" />
					</label>
					<label>
						<span>{{ t("contactResearchPage.form.lastName") }}{{ contactNameRequired ? " *" : "" }}</span>
						<input v-model="draft.lastName" :required="contactNameRequired" autocomplete="family-name" />
					</label>
					<label class="wide">
						<span>{{ t("contactResearchPage.form.companyName") }}{{ companyRequired ? " *" : "" }}</span>
						<input v-model="draft.companyName" :required="companyRequired" autocomplete="organization" />
						<small v-if="companyRequired">{{ t("contactResearchPage.form.companyRequired") }}</small>
					</label>
					<label class="wide">
						<span>{{ t("contactResearchPage.form.linkedInUrl") }}</span>
						<input v-model="draft.contactLinkedInUrl" type="url" autocomplete="url" />
						<small class="field-hint">{{ t("contactResearchPage.form.linkedInHint") }}</small>
					</label>
				</div>
				<AccordionRoot class="input-accordions" persistence-key="contact-research-input-accordion">
					<AccordionItem value="company-details" class="input-accordion">
						<template #title>{{ t("contactResearchPage.form.companyTitle") }}</template>
						<div class="company-fields">
							<label class="wide">
								<span>{{ t("contactResearchPage.form.companyDomain") }}</span>
								<input v-model="draft.companyDomain" autocomplete="url" />
							</label>
							<label class="wide">
								<span>{{ t("contactResearchPage.form.companyAddress") }}</span>
								<input v-model="draft.companyAddress" autocomplete="street-address" />
							</label>
							<label>
								<span>{{ t("contactResearchPage.form.companyPhone") }}</span>
								<input v-model="draft.companyPhone" type="tel" autocomplete="tel" />
							</label>
							<label>
								<span>{{ t("contactResearchPage.form.companyNeq") }}</span>
								<input v-model="draft.companyNeq" inputmode="numeric" />
							</label>
						</div>
					</AccordionItem>
					<AccordionItem value="contact-location" class="input-accordion">
						<template #title>{{ t("contactResearchPage.form.contactLocation") }}</template>
						<template #header-actions>
							<span class="accordion-summary">{{ contactLocationSummary }}</span>
						</template>
						<div class="location-fields">
							<label>
								<span>{{ t("contactResearchPage.form.municipality") }}</span>
								<input v-model="draft.contactMunicipality" autocomplete="address-level2" />
							</label>
							<label>
								<span>{{ t("contactResearchPage.form.province") }}</span>
								<input v-model="draft.contactProvince" autocomplete="address-level1" />
							</label>
							<label>
								<span>{{ t("contactResearchPage.form.postalCode") }}</span>
								<input v-model="draft.contactPostalCode" autocomplete="postal-code" />
							</label>
							<label class="wide">
								<span>{{ t("contactResearchPage.form.address") }}</span>
								<input v-model="draft.contactAddress" autocomplete="street-address" />
							</label>
						</div>
					</AccordionItem>
				</AccordionRoot>
				<ProviderSelector
					:providers="displayedProviders"
					:selected-provider-ids="selectedProviders"
					:loading="loadingProviders"
					@toggle="toggleProvider"
				/>
				<Button class="search-button" variant="primary" size="l" type="submit" :disabled="!canSearch">
					<AppIcon :name="IconName.Search" :size="16" />
					{{ searchActionLabel }}
				</Button>
			</section>
		</form>
	</div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from "vue"
import { useI18n } from "vue-i18n"
import AppIcon from "@/components/AppIcon.vue"
import AccordionItem from "@/components/ui/AccordionItem.vue"
import AccordionRoot from "@/components/ui/AccordionRoot.vue"
import Button from "@/components/ui/Button.vue"
import ProviderSelector from "@/components/contact-research/ProviderSelector.vue"
import { IconName } from "@/components/icons/registry"
import {
	ContactResearchProviderId,
	type ContactResearchInput,
	type ContactResearchProviderInfo,
} from "@/types/contactResearch"

const props = defineProps<{
	input: ContactResearchInput
	providers: ContactResearchProviderInfo[]
	selectedProviderIds: ContactResearchProviderId[]
	loadingProviders: boolean
	providerLoadError: string
	error: string | null
}>()

const emit = defineEmits<{
	run: [input: ContactResearchInput, providerIds: ContactResearchProviderId[]]
}>()

const providerDisplayOrder = [
	ContactResearchProviderId.Hunter,
	ContactResearchProviderId.Canada411,
	ContactResearchProviderId.Surfe,
	ContactResearchProviderId.WebSearch,
]

const { t } = useI18n()
const draft = reactive<ContactResearchInput>({ ...props.input })
const selectedProviders = ref([...props.selectedProviderIds])

const displayedProviders = computed(() =>
	[...props.providers].sort(
		(firstProvider, secondProvider) =>
			providerDisplayOrder.indexOf(firstProvider.id) - providerDisplayOrder.indexOf(secondProvider.id)
	)
)

const isLinkedInOnlySearch = computed(() => {
	return (
		selectedProviders.value.length === 1 &&
		selectedProviders.value[0] === ContactResearchProviderId.Surfe &&
		Boolean(draft.contactLinkedInUrl?.trim())
	)
})

const contactNameRequired = computed(() => !isLinkedInOnlySearch.value)

const contactLocationSummary = computed(() => {
	const values = [draft.contactMunicipality, draft.contactProvince, draft.contactPostalCode, draft.contactAddress]

	const location = [draft.contactMunicipality, draft.contactProvince]
		.map(value => value?.trim())
		.filter(Boolean)
		.join(", ")

	const emptyFieldCount = values.filter(value => !value?.trim()).length
	const summary = location ? [location] : []

	if (emptyFieldCount > 0) {
		const key = emptyFieldCount === 1 ? "emptyField" : "emptyFields"
		summary.push(t(`contactResearchPage.form.${key}`, { count: emptyFieldCount }))
	}

	return summary.join(" · ")
})

const companyRequired = computed(() => {
	if (selectedProviders.value.length === 0) {
		return false
	}

	return selectedProviders.value.every(providerRequiresCompany)
})
const canSearch = computed(() => {
	const hasRequiredContact =
		isLinkedInOnlySearch.value || (draft.firstName.trim().length > 0 && draft.lastName.trim().length > 0)

	const hasRequiredCompany = !companyRequired.value || Boolean(draft.companyName?.trim())

	return hasRequiredContact && hasRequiredCompany && selectedProviders.value.length > 0 && !props.loadingProviders
})
const searchActionLabel = computed(() => {
	const key = selectedProviders.value.length === 1 ? "searchOnSource" : "searchOnSources"

	return t(`contactResearchPage.actions.${key}`, { count: selectedProviders.value.length })
})

watch(
	() => props.selectedProviderIds,
	providerIds => {
		selectedProviders.value = [...providerIds]
	}
)

function providerRequiresCompany(providerId: ContactResearchProviderId): boolean {
	const requiresCompany = props.providers.find(provider => provider.id === providerId)?.requiresCompany ?? false
	const hasLinkedInUrl = providerId === ContactResearchProviderId.Surfe && Boolean(draft.contactLinkedInUrl?.trim())

	return requiresCompany && !hasLinkedInUrl
}

function toggleProvider(providerId: ContactResearchProviderId): void {
	if (selectedProviders.value.includes(providerId)) {
		selectedProviders.value = selectedProviders.value.filter(selectedProvider => selectedProvider !== providerId)

		return
	}

	selectedProviders.value = [...selectedProviders.value, providerId]
}

function runSearch(): void {
	if (!canSearch.value) {
		return
	}

	emit("run", { ...draft }, selectedProviders.value)
}
</script>

<style scoped>
.input-state {
	width: min(100%, 1024px);
	margin: 0 auto;
}

.page-header {
	margin-bottom: var(--spacing-lg);
	text-align: center;

	h1,
	p {
		margin: 0;
	}

	h1 {
		font-size: var(--font-size-3xl);
		font-weight: var(--font-weight-semibold);
	}

	p {
		margin-top: var(--spacing-sm);
		font-size: var(--font-size-base);
		color: var(--neutral-600);
	}
}

.panel {
	min-width: 0;
	padding: var(--spacing-md);
	background: var(--neutral-0);
	border: var(--border-width-thin) solid var(--neutral-200);
	border-radius: var(--border-radius-md);
	box-shadow: var(--shadow-sm);
}

.fields {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: var(--space-14);
	margin-top: var(--space-16);
}

.fields,
.location-fields,
.company-fields {
	label {
		display: grid;
		gap: var(--space-6);
		font-size: var(--font-size-small);
		font-weight: var(--font-weight-medium);
	}

	.wide {
		grid-column: 1 / -1;
	}

	input {
		width: 100%;
		min-width: 0;
		height: var(--height-search-input);
		padding: 0 var(--space-12);
		font: inherit;
		font-weight: var(--font-weight-regular);
		color: var(--neutral-900);
		background: var(--neutral-0);
		border: var(--border-width-thin) solid var(--neutral-400);
		border-radius: var(--border-radius-base);
		outline: none;
		box-sizing: border-box;

		&:focus {
			border-color: var(--primary-green);
			box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary-green) 18%, transparent);
		}
	}
}

.fields {
	small {
		color: var(--accent-target);
		font-size: var(--font-size-xs);
		font-weight: var(--font-weight-regular);
	}

	.field-hint {
		color: var(--neutral-600);
	}
}

.input-accordions {
	gap: 0;
	margin-top: var(--space-16);
}

.input-accordion {
	border-right: 0;
	border-left: 0;
	border-radius: 0;
	box-shadow: none;

	.accordion-summary {
		font-size: var(--font-size-xs);
		color: var(--neutral-600);
	}

	& + & {
		border-top: 0;
	}
}

.input-accordion:deep(.accordion-header) {
	padding-right: 0;
}

.input-accordion:deep(.accordion-header .accordion-trigger) {
	padding-left: 0;
}

.input-accordion:deep(.accordion-legend) {
	font-size: var(--font-size-small);
	font-weight: var(--font-weight-semibold);
}

.input-accordion:deep(.accordion-content-wrapper .accordion-content) {
	padding: 0;
	border-top: 0;
}

.input-accordion:deep(.accordion-content-wrapper[data-state="open"]) {
	height: auto;
	animation: none;
}

.location-fields,
.company-fields {
	display: grid;
	gap: var(--space-14);
	padding-bottom: var(--space-16);
}

.location-fields {
	grid-template-columns: minmax(0, 1fr) var(--space-100) var(--space-120);
}

.company-fields {
	grid-template-columns: repeat(2, minmax(0, 1fr));
}

.search-button {
	width: 100%;
	gap: var(--space-10);
	margin-top: var(--space-24);
}

.alert {
	padding: var(--space-12) var(--space-14);
	margin-bottom: var(--space-16);
	font-size: var(--font-size-small);
	border: var(--border-width-thin) solid;
	border-radius: var(--border-radius-base);

	&.error {
		color: var(--accent-warning);
		background: color-mix(in srgb, var(--accent-warning) 7%, white);
		border-color: color-mix(in srgb, var(--accent-warning) 25%, white);
	}
}

@media (max-width: 640px) {
	.fields,
	.location-fields,
	.company-fields {
		grid-template-columns: 1fr;

		.wide {
			grid-column: auto;
		}
	}
}
</style>
